import { useEffect, useRef } from "react"
import { useBlocker } from "react-router-dom"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"

/**
 * Asks before leaving the manual reschedule workspace with pairings not yet applied (AB#811, decision
 * ams-1rd: pairings are not kept, so leaving loses them). Links and Back inside the app are held by
 * useBlocker and confirmed here; a reload or a closed tab gets the browser's own prompt through
 * beforeunload. As benefit-edit-scope.tsx does. Needs a data router, which App.tsx provides.
 */
export function UnappliedChangesGuard({ when, count }: { when: boolean; count: number }) {
  const active = useRef(when)
  active.current = when

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    active.current && currentLocation.pathname !== nextLocation.pathname)

  useEffect(() => {
    const beforeUnload = (event: BeforeUnloadEvent) => {
      if (!active.current) return
      event.preventDefault()
      event.returnValue = ""
    }
    window.addEventListener("beforeunload", beforeUnload)
    return () => window.removeEventListener("beforeunload", beforeUnload)
  }, [])

  // Nothing left to lose (applied, or every pairing undone) while a navigation waits: let it go.
  useEffect(() => {
    if (blocker.state === "blocked" && !when) blocker.proceed()
  }, [blocker, when])

  const blocked = blocker.state === "blocked"

  return (
    <AlertDialog open={blocked} onOpenChange={(open) => !open && blocked && blocker.reset()}>
      <AlertDialogContent data-testid="leave-guard">
        <AlertDialogHeader>
          <AlertDialogTitle>Leave without applying?</AlertDialogTitle>
          <AlertDialogDescription>
            {count === 1 ? "1 change has" : `${count} changes have`} not been applied. Leaving this page discards
            {count === 1 ? " it" : " them"}; the appointments stay where they are.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => blocked && blocker.reset()}>Stay on this page</AlertDialogCancel>
          <AlertDialogAction onClick={() => blocked && blocker.proceed()}>Leave and discard</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}
